import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';

export interface LunaFaceProps {
  faceId?: string;
  customAvatarUrl?: string;
  isListening?: boolean;
  isSpeaking?: boolean;
  isWaving?: boolean;
  sentiment?: string;
  className?: string;
  themeColor?: string;
}

export const LUNA_FACE_CATALOG = [
  {
    id: 'cute_chibi_bot',
    name: 'Cute Chibi 3D Spherical Bot',
    tagline: 'Hyper-Realistic 3D Flying Android Bot',
    badge: 'REAL 3D',
    color: 'text-emerald-400',
    borderColor: 'border-emerald-400/60',
    glowColor: 'rgba(16,185,129,0.6)',
    bgGradient: 'from-emerald-950/60 via-slate-950/40 to-black',
    description: 'Hyper-realistic 3D ceramic spherical bot with floating flight physics, live voice lip-sync visemes, expressive arm pod gestures, glossy 3D visor glass, and green hover jet propulsion.'
  },
  {
    id: 'holo_android',
    name: 'Holographic Cyber Android 3D',
    tagline: '3D Vector Animated Cyber Girl Android',
    badge: 'PREMIUM 3D',
    color: 'text-cyan-300',
    borderColor: 'border-cyan-400/60',
    glowColor: 'rgba(6,182,212,0.6)',
    bgGradient: 'from-cyan-950/60 via-purple-950/40 to-black',
    description: '100% Programmatic 3D Holographic Cyber Girl Android featuring dotted wireframe mesh geometry, chest reactor core, revolving 3D telemetry orbital rings, and floating HUD cards.'
  },
  {
    id: 'cyber_cat',
    name: 'Cyber Cat AI',
    tagline: 'Classic Glowing Cyberpunk Cat Avatar',
    badge: 'ORIGINAL',
    color: 'text-cyan-400',
    borderColor: 'border-cyan-500/40',
    glowColor: 'rgba(6,182,212,0.4)',
    bgGradient: 'from-cyan-950/40 to-purple-950/40',
    description: 'Animated cyberpunk feline avatar with glowing ears, expressive blinking eyes, blushing cheeks, and audio-reactive voice pulse.'
  },
  {
    id: 'quantum_orb',
    name: 'Quantum AI Energy Orb',
    tagline: 'Holographic 3D Energy Core',
    badge: 'FUTURISTIC',
    color: 'text-purple-400',
    borderColor: 'border-purple-500/40',
    glowColor: 'rgba(168,85,247,0.4)',
    bgGradient: 'from-purple-950/40 to-indigo-950/40',
    description: 'Multi-layer quantum energy sphere with rotating orbital rings, core particle pulse, and audio frequency reaction waves.'
  },
  {
    id: 'holo_anime',
    name: 'Holographic Anime AI',
    tagline: 'High-Tech Anime AI Companion',
    badge: 'NEW',
    color: 'text-pink-400',
    borderColor: 'border-pink-500/40',
    glowColor: 'rgba(236,72,153,0.4)',
    bgGradient: 'from-pink-950/40 to-rose-950/40',
    description: 'Futuristic holographic anime avatar face with glowing cyber visor mark, animated expressive eyes, blushing cheeks, and speech equalizer.'
  },
  {
    id: 'neon_synth',
    name: 'Neon Cyber Visor',
    tagline: 'Matrix Spectrum Equalizer Avatar',
    badge: 'CYBER',
    color: 'text-emerald-400',
    borderColor: 'border-emerald-500/40',
    glowColor: 'rgba(16,185,129,0.4)',
    bgGradient: 'from-emerald-950/40 to-teal-950/40',
    description: 'Industrial cyberpunk visor avatar with live LED equalizer bar graph expression, animated radar scanner, and neon matrix aura.'
  },
  {
    id: 'minimal_core',
    name: 'Minimalist Fluid Core',
    tagline: 'Sleek Modern Fluid Wave Sphere',
    badge: 'MINIMAL',
    color: 'text-amber-400',
    borderColor: 'border-amber-500/40',
    glowColor: 'rgba(245,158,11,0.4)',
    bgGradient: 'from-amber-950/40 to-orange-950/40',
    description: 'Clean modern fluid wave sphere with dynamic radial gradient displacement and audio wave ripple rings.'
  }
];

// Global Active Face State & Subscriber Hook
let globalActiveFaceId = typeof localStorage !== 'undefined' ? localStorage.getItem('kangavena:active_luna_face') || 'cute_chibi_bot' : 'cute_chibi_bot';

export const setGlobalActiveLunaFace = (faceId: string) => {
  globalActiveFaceId = faceId;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('kangavena:active_luna_face', faceId);
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('luna-face-changed', { detail: { faceId } }));
  }
};

export const LunaFace = ({
  faceId,
  customAvatarUrl,
  isListening = false,
  isSpeaking = false,
  isWaving = false,
  sentiment = 'neutral',
  className = 'w-10 h-10',
  themeColor
}: LunaFaceProps) => {
  const [activeFace, setActiveFace] = useState<string>(faceId || globalActiveFaceId);

  useEffect(() => {
    if (faceId) {
      setActiveFace(faceId);
      return;
    }

    // Fetch active global face from API on initial mount
    fetch('/api/luna/active-face')
      .then(res => res.json())
      .then(data => {
        if (data?.setting?.activeFaceId) {
          setActiveFace(data.setting.activeFaceId);
          localStorage.setItem('kangavena:active_luna_face', data.setting.activeFaceId);
        }
      })
      .catch(() => {});

    const handleFaceChange = (e: any) => {
      if (e.detail?.faceId) {
        setActiveFace(e.detail.faceId);
      }
    };

    window.addEventListener('luna-face-changed', handleFaceChange);
    return () => window.removeEventListener('luna-face-changed', handleFaceChange);
  }, [faceId]);

  const effectiveFace = faceId || activeFace || 'cute_chibi_bot';

  const blinkAnimation = {
    scaleY: [1, 1, 0.1, 1, 1],
    transition: { duration: 4, repeat: Infinity, times: [0, 0.9, 0.95, 0.98, 1] }
  };
  
  // ── UNIFIED SEAMLESS HOVER LEVITATION (ZERO POSITION SNAPPING) ────────────
  const flightAnimation = {
    y: [0, -5, 0],
    x: [0, 1, -1, 0],
    rotateZ: [0, -0.5, 0.5, 0],
    transition: { duration: 4.5, repeat: Infinity, ease: "easeInOut" }
  };

  const bobAnimation = flightAnimation;

  // ── ARM POD GESTURE ANIMATIONS (FLUID TRANSITION) ─────────────────────────
  const leftArmGesture = {
    rotateZ: isSpeaking ? [0, -5, 0] : [0, -2, 0],
    y: [0, -2, 0],
    transition: { duration: 4, repeat: Infinity, ease: "easeInOut" }
  };

  const rightArmGesture = isWaving ? {
    rotateZ: [0, -135, -112, -135, -112, 0],
    transition: { duration: 1.8, repeat: 1, ease: "easeInOut" }
  } : {
    rotateZ: isSpeaking ? [0, 3, 0] : [0, 0, 0],
    y: [0, -1, 0],
    transition: { duration: 4, repeat: Infinity, ease: "easeInOut" }
  };

  const voicePulse = (isListening || isSpeaking) ? {
    opacity: [0.35, 0.75, 0.35],
    scale: [0.95, 1.25, 0.95],
    transition: { duration: 0.8, repeat: Infinity, ease: "easeInOut" }
  } : { opacity: 0.15, scale: 1 };

  const isCustomPhoto = effectiveFace === 'custom_avatar' ||
    effectiveFace === 'custom_photo' ||
    effectiveFace === 'custom_bespoke_3d' ||
    effectiveFace === 'custom_saara_teacher' ||
    effectiveFace?.startsWith('/') || 
    effectiveFace?.startsWith('http') || 
    (Boolean(customAvatarUrl) && !['holo_android', 'cyber_cat', 'quantum_orb', 'holo_anime', 'neon_synth', 'minimal_core'].includes(effectiveFace));
  if (isCustomPhoto) {
    const photoSrc = customAvatarUrl || (effectiveFace === 'custom_saara_teacher' ? '/uploads/luna-assets/saara_teacher_avatar.png' : (effectiveFace?.startsWith('/') || effectiveFace?.startsWith('http') ? effectiveFace : undefined));
    if (photoSrc) {
    return (
      <motion.div 
        className={`relative flex items-center justify-center ${className}`}
        animate={bobAnimation}
      >
        <div className="relative w-full h-full max-w-full max-h-full aspect-square flex items-center justify-center">
          {/* Audio Reactive Pulsing Rings */}
          {(isListening || isSpeaking) && (
            <>
              <motion.div 
                className="absolute -inset-3 sm:-inset-4 rounded-full border-2 border-cyan-400/60 pointer-events-none"
                animate={{ scale: [1, 1.35], opacity: [0.8, 0] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: "easeOut" }}
              />
              <motion.div 
                className="absolute -inset-5 sm:-inset-7 rounded-full border border-emerald-400/40 pointer-events-none"
                animate={{ scale: [1, 1.45], opacity: [0.6, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut", delay: 0.3 }}
              />
            </>
          )}

          {/* Outer Cyber Gradient Glow Ring */}
          <div className="absolute -inset-1 rounded-full bg-gradient-to-tr from-cyan-500 via-amber-400 to-emerald-400 opacity-75 blur-xs animate-[spin_8s_linear_infinite]" />

          {/* Circular Avatar Image */}
          <div className="relative z-10 w-full h-full rounded-full overflow-hidden border-2 border-white/90 shadow-[0_0_25px_rgba(245,158,11,0.6)] bg-slate-950 flex items-center justify-center">
            <img 
              src={photoSrc} 
              alt="AI Avatar" 
              className="w-full h-full object-cover object-top select-none pointer-events-none"
            />
          </div>
        </div>
      </motion.div>
    );
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 1. HYPER-REALISTIC 3D CUTE CHIBI SPHERICAL BOT (FLIGHT & LIP-SYNC ENGINE)
  // ═══════════════════════════════════════════════════════════════════════════
  if (effectiveFace === 'cute_chibi_bot') {
    return (
      <motion.svg viewBox="0 0 200 260" className={className} animate={flightAnimation}>
        <defs>
          {/* Lightweight High-Performance Glow Filter */}
          <filter id="chibiGlowExtreme" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>

          {/* 3D Glossy Ceramic White Head Gradient */}
          <radialGradient id="chibiHeadGloss" cx="35%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="45%" stopColor="#f1f5f9" />
            <stop offset="80%" stopColor="#cbd5e1" />
            <stop offset="100%" stopColor="#94a3b8" />
          </radialGradient>

          {/* 3D Body Shading Gradient */}
          <radialGradient id="chibiBodyShade" cx="40%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="60%" stopColor="#e2e8f0" />
            <stop offset="90%" stopColor="#94a3b8" />
            <stop offset="100%" stopColor="#64748b" />
          </radialGradient>

          {/* 3D Visor Glass Gradient with Specular Reflection */}
          <radialGradient id="chibiVisorGloss" cx="45%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#0f2347" />
            <stop offset="60%" stopColor="#071126" />
            <stop offset="100%" stopColor="#020612" />
          </radialGradient>

          {/* Specular Glare Arc */}
          <linearGradient id="chibiSpecularGlare" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.45" />
            <stop offset="50%" stopColor="#ffffff" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>

          {/* Futuristic Ion Hover Flame Gradient */}
          <linearGradient id="plasmaHoverFlameGrad" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
            <stop offset="35%" stopColor="#38bdf8" stopOpacity="0.85" />
            <stop offset="70%" stopColor="#06b6d4" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#0284c7" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Soft Ambient Anti-Gravity Floor Halo */}
        <motion.ellipse 
          cx="100" cy="226" rx="42" ry="9" 
          fill="#06b6d4" 
          fillOpacity="0.35"
          filter="url(#chibiGlowExtreme)"
          animate={{ scale: [0.92, 1.1, 0.92], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: '100px 226px' }}
        />

        {/* Anti-Gravity Energy Levitation Base */}
        <g style={{ transformOrigin: '100px 207px' }}>
          {/* Outer Pulsing Energy Ring */}
          <motion.ellipse 
            cx="100" cy="214" rx="32" ry="7" 
            fill="none" 
            stroke="#06b6d4" 
            strokeWidth="2.5" 
            filter="url(#chibiGlowExtreme)"
            animate={{ scale: [0.94, 1.1, 0.94], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: '100px 214px' }}
          />

          {/* Under-Body Metallic Nozzle Port (Mounted Flush) */}
          <ellipse cx="100" cy="207" rx="22" ry="6" fill="url(#chibiBodyShade)" stroke="#64748b" strokeWidth="1.5" />
          <ellipse cx="100" cy="207" rx="15" ry="3.5" fill="#06b6d4" filter="url(#chibiGlowExtreme)" opacity="0.95" />
        </g>

        {/* ── FLOATING 3D WING POD ARMS (GESTURE ENGINE) ────────────────── */}
        {/* Left Arm Pod */}
        <motion.g animate={leftArmGesture} style={{ transformOrigin: '40px 140px' }}>
          <path d="M 38 130 Q 22 160 30 190 Q 48 175 46 142 Z" fill="url(#chibiBodyShade)" stroke="#94a3b8" strokeWidth="1.5" />
          <ellipse cx="36" cy="145" rx="3" ry="8" fill="#06b6d4" opacity="0.7" />
        </motion.g>

        {/* ── MAIN SPHERICAL BODY ────────────────────────────────────────── */}
        <ellipse cx="100" cy="162" rx="46" ry="44" fill="url(#chibiBodyShade)" stroke="#94a3b8" strokeWidth="2.5" />
        
        {/* 3D Specular Highlight on Body */}
        <ellipse cx="88" cy="144" rx="22" ry="14" fill="url(#chibiSpecularGlare)" />

        {/* Right Arm Pod (Locks to 160px 130px Shoulder Joint & Swings Up to Wave "Hi") */}
        <motion.g animate={rightArmGesture} style={{ transformOrigin: '160px 130px' }}>
          <path d="M 160 132 Q 176 158 170 186 Q 154 172 154 140 Z" fill="url(#chibiBodyShade)" stroke="#94a3b8" strokeWidth="1.5" />
          <path d="M 162 145 Q 166 162 163 175" stroke="#06b6d4" strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
        </motion.g>

        {/* Chest Reactor Triangle Emblem with Multi-Layer Glow */}
        <g style={{ transformOrigin: '100px 165px' }}>
          <polygon points="100,150 114,172 86,172" fill="#06b6d4" filter="url(#chibiGlowExtreme)" />
          <polygon points="100,154 110,170 90,170" fill="#ffffff" className="animate-pulse" />
          <polygon points="100,157 106,168 94,168" fill="#06b6d4" />
        </g>

        {/* Neck Ring Joint */}
        <ellipse cx="100" cy="116" rx="24" ry="7" fill="#0f172a" stroke="#334155" strokeWidth="1.5" />

        {/* ── 3D SPHERICAL HEAD & HIGH-GLOSS VISOR ───────────────────────── */}
        {/* Head Base Shell */}
        <circle cx="100" cy="68" r="58" fill="url(#chibiHeadGloss)" stroke="#94a3b8" strokeWidth="3" />
        
        {/* 3D Specular Highlight on Head Top */}
        <ellipse cx="84" cy="38" rx="30" ry="16" fill="url(#chibiSpecularGlare)" />

        {/* Ear Pod Headphones (Left & Right) */}
        <g stroke="#94a3b8" strokeWidth="2.5">
          {/* Left Ear Pod */}
          <ellipse cx="42" cy="68" rx="8" ry="22" fill="url(#chibiBodyShade)" />
          <ellipse cx="42" cy="68" rx="4" ry="15" fill="#06b6d4" filter="url(#chibiGlowExtreme)" />
          <line x1="42" y1="46" x2="36" y2="24" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="36" cy="24" r="3.5" fill="#06b6d4" filter="url(#chibiGlowExtreme)" />

          {/* Right Ear Pod */}
          <ellipse cx="158" cy="68" rx="8" ry="22" fill="url(#chibiBodyShade)" />
          <ellipse cx="158" cy="68" rx="4" ry="15" fill="#06b6d4" filter="url(#chibiGlowExtreme)" />
          <line x1="158" y1="46" x2="164" y2="24" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="164" cy="24" r="3.5" fill="#06b6d4" filter="url(#chibiGlowExtreme)" />
        </g>

        {/* Shiny Black Visor Screen Glass */}
        <ellipse cx="100" cy="68" rx="44" ry="34" fill="url(#chibiVisorGloss)" stroke="#06b6d4" strokeWidth="2" strokeOpacity="0.8" />
        
        {/* Visor Glare Curved Reflection Arc */}
        <path d="M 64 44 Q 100 34 136 44 A 42 42 0 0 0 64 44 Z" fill="url(#chibiSpecularGlare)" />

        {/* ── LIVE VOICE LIP-SYNC & LED MATRIX VISOR EXPRESSIONS ───────────── */}
        <motion.g animate={{ scale: (isListening || isSpeaking) ? 1.08 : 1 }} style={{ transformOrigin: '100px 66px' }}>
          {/* Left Eye */}
          <motion.ellipse cx="80" cy="65" rx="8.5" ry="13" fill="#06b6d4" filter="url(#chibiGlowExtreme)" animate={blinkAnimation} style={{ transformOrigin: '80px 65px' }} />
          <ellipse cx="80" cy="65" rx="5" ry="8" fill="#ffffff" />
          <circle cx="78" cy="61" r="2.5" fill="#ffffff" />

          {/* Right Eye */}
          <motion.ellipse cx="120" cy="65" rx="8.5" ry="13" fill="#06b6d4" filter="url(#chibiGlowExtreme)" animate={blinkAnimation} style={{ transformOrigin: '120px 65px' }} />
          <ellipse cx="120" cy="65" rx="5" ry="8" fill="#ffffff" />
          <circle cx="118" cy="61" r="2.5" fill="#ffffff" />

          {/* REAL-TIME VOICE LIP-SYNC VISEME MOUTH */}
          {isSpeaking ? (
            <motion.path 
              d="M 88 83 Q 100 95 112 83 Q 100 78 88 83 Z"
              fill="#06b6d4" 
              stroke="#ffffff" 
              strokeWidth="1.5"
              strokeLinejoin="round"
              filter="url(#chibiGlowExtreme)"
              animate={{ 
                d: [
                  "M 88 83 Q 100 92 112 83 Q 100 80 88 83 Z",
                  "M 88 83 Q 100 96 112 83 Q 100 76 88 83 Z",
                  "M 88 84 Q 100 89 112 84 Q 100 82 88 84 Z",
                  "M 88 83 Q 100 94 112 83 Q 100 78 88 83 Z"
                ]
              }}
              transition={{ duration: 0.48, repeat: Infinity, ease: "easeInOut" }}
            />
          ) : isListening ? (
            <motion.path 
              d="M 88 84 Q 100 92 112 84" 
              fill="none" 
              stroke="#06b6d4" 
              strokeWidth="3.5" 
              strokeLinecap="round" 
              filter="url(#chibiGlowExtreme)"
              animate={{ strokeWidth: [3, 5, 3] }}
              transition={{ duration: 0.6, repeat: Infinity }}
            />
          ) : (
            <path 
              d="M 88 84 Q 100 91 112 84" 
              fill="none" 
              stroke="#06b6d4" 
              strokeWidth="3" 
              strokeLinecap="round" 
              filter="url(#chibiGlowExtreme)" 
            />
          )}
        </motion.g>

      </motion.svg>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 2. PURE 3D PROGRAMMATIC HOLOGRAPHIC CYBER ANDROID GIRL AVATAR
  // ═══════════════════════════════════════════════════════════════════════════
  if (effectiveFace === 'holo_android') {
    return (
      <motion.svg viewBox="0 0 200 360" className={className} animate={flightAnimation}>
        <defs>
          <pattern id="androidMeshGrid" width="4" height="4" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="0.6" fill="currentColor" fillOpacity="0.7" />
          </pattern>
          <filter id="holoGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        <motion.ellipse 
          cx="100" cy="180" rx="75" ry="150" 
          fill="currentColor" 
          animate={voicePulse} 
          className="blur-3xl" 
        />

        <g style={{ transformOrigin: '100px 120px' }}>
          <motion.ellipse 
            cx="100" cy="120" rx="85" ry="24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeDasharray="20 12 8 12" 
            strokeOpacity="0.85"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            style={{ transformOrigin: '100px 120px' }}
          />
          <motion.ellipse 
            cx="100" cy="120" rx="85" ry="24" 
            fill="none" 
            stroke="#ffffff" 
            strokeWidth="2.5" 
            strokeDasharray="4 80" 
            strokeLinecap="round"
            animate={{ rotate: [0, -360] }}
            transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            style={{ transformOrigin: '100px 120px' }}
          />
        </g>

        <g style={{ transformOrigin: '100px 170px' }}>
          <motion.ellipse 
            cx="100" cy="170" rx="92" ry="28" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="1.8" 
            strokeDasharray="30 16 10 16" 
            strokeOpacity="0.75"
            animate={{ rotate: [-20, 340] }}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            style={{ transformOrigin: '100px 170px' }}
          />
        </g>

        <motion.g animate={{ y: [0, -3, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
          <rect x="10" y="80" width="40" height="24" rx="3" fill="#040914" fillOpacity="0.85" stroke="currentColor" strokeWidth="1" strokeOpacity="0.6" />
          <text x="14" y="89" fill="currentColor" fontSize="4.5" fontFamily="monospace" fontWeight="bold">PARAMETER</text>
          <line x1="14" y1="94" x2="44" y2="94" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.5" />
          <line x1="14" y1="98" x2="38" y2="98" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.5" />
          <line x1="14" y1="100" x2="32" y2="100" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.5" />
        </motion.g>

        <motion.g animate={{ y: [0, 3, 0] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}>
          <rect x="150" y="70" width="42" height="28" rx="3" fill="#040914" fillOpacity="0.85" stroke="currentColor" strokeWidth="1" strokeOpacity="0.6" />
          <text x="154" y="79" fill="currentColor" fontSize="4" fontFamily="monospace" fontWeight="bold">TELEMETRY</text>
          <g>
            <motion.rect x="156" y="84" width="3" height="10" fill="currentColor" initial={{ height: 10 }} animate={{ height: [6, 12, 4, 10] }} transition={{ duration: 0.5, repeat: Infinity }} />
            <motion.rect x="162" y="82" width="3" height="12" fill="currentColor" initial={{ height: 12 }} animate={{ height: [10, 6, 12, 8] }} transition={{ duration: 0.6, repeat: Infinity }} />
            <motion.rect x="168" y="85" width="3" height="9" fill="currentColor" initial={{ height: 9 }} animate={{ height: [4, 12, 6, 10] }} transition={{ duration: 0.4, repeat: Infinity }} />
            <motion.rect x="174" y="83" width="3" height="11" fill="currentColor" initial={{ height: 11 }} animate={{ height: [8, 4, 12, 6] }} transition={{ duration: 0.7, repeat: Infinity }} />
            <motion.rect x="180" y="86" width="3" height="8" fill="currentColor" initial={{ height: 8 }} animate={{ height: [5, 10, 4, 8] }} transition={{ duration: 0.5, repeat: Infinity }} />
          </g>
        </motion.g>

        <g stroke="currentColor" strokeWidth="1.2" strokeOpacity="0.85">
          <ellipse cx="100" cy="50" rx="24" ry="28" fill="#040914" fillOpacity="0.85" />
          <ellipse cx="100" cy="50" rx="24" ry="28" fill="url(#androidMeshGrid)" />

          <rect x="72" y="44" width="6" height="14" rx="3" fill="currentColor" fillOpacity="0.4" />
          <rect x="122" y="44" width="6" height="14" rx="3" fill="currentColor" fillOpacity="0.4" />
          <line x1="75" y1="44" x2="70" y2="28" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="125" y1="44" x2="130" y2="28" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="70" cy="28" r="2.5" fill="#ffffff" filter="url(#holoGlow)" />
          <circle cx="130" cy="28" r="2.5" fill="#ffffff" filter="url(#holoGlow)" />

          <motion.g animate={{ scale: (isListening || isSpeaking) ? 1.05 : 1 }} style={{ transformOrigin: '100px 48px' }}>
            <motion.ellipse cx="89" cy="48" rx="5.5" ry="7" fill="#ffffff" filter="url(#holoGlow)" animate={blinkAnimation} style={{ transformOrigin: '89px 48px' }} />
            <motion.ellipse cx="111" cy="48" rx="5.5" ry="7" fill="#ffffff" filter="url(#holoGlow)" animate={blinkAnimation} style={{ transformOrigin: '111px 48px' }} />
            <circle cx="89" cy="48" r="2" fill="currentColor" />
            <circle cx="111" cy="48" r="2" fill="currentColor" />

            <path d={isSpeaking ? "M 93 63 Q 100 70 107 63 Z" : "M 94 63 Q 100 68 106 63"} fill={isSpeaking ? "currentColor" : "none"} strokeWidth="2" strokeLinecap="round" />
          </motion.g>

          <path d="M 94 76 L 94 88 L 106 88 L 106 76 Z" fill="#040914" fillOpacity="0.9" />
          <line x1="94" y1="82" x2="106" y2="82" strokeOpacity="0.5" />

          <path d="M 76 90 Q 100 84 124 90 L 132 145 Q 100 152 68 145 Z" fill="#040914" fillOpacity="0.85" />
          <path d="M 76 90 Q 100 84 124 90 L 132 145 Q 100 152 68 145 Z" fill="url(#androidMeshGrid)" />

          <circle cx="72" cy="95" r="9" fill="currentColor" fillOpacity="0.2" />
          <circle cx="128" cy="95" r="9" fill="currentColor" fillOpacity="0.2" />

          <path d="M 66 98 L 48 140 L 40 180" fill="none" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 134 98 L 152 140 L 160 180" fill="none" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 38 180 Q 42 192 36 198" fill="none" strokeWidth="2" />

          <path d="M 74 146 L 126 146 L 118 190 L 82 190 Z" fill="#040914" fillOpacity="0.85" />
          <path d="M 74 146 L 126 146 L 118 190 L 82 190 Z" fill="url(#androidMeshGrid)" />

          <path d="M 84 192 L 78 260 L 82 320" fill="none" strokeWidth="3" />
          <path d="M 116 192 L 122 260 L 118 320" fill="none" strokeWidth="3" />

          <circle cx="78" cy="260" r="6" fill="currentColor" fillOpacity="0.3" />
          <circle cx="122" cy="260" r="6" fill="currentColor" fillOpacity="0.3" />

          <path d="M 76 320 Q 64 340 82 344 Z" fill="currentColor" fillOpacity="0.5" />
          <path d="M 118 320 Q 130 340 114 344 Z" fill="currentColor" fillOpacity="0.5" />
        </g>

        <g style={{ transformOrigin: '100px 115px' }}>
          <motion.circle 
            cx="100" cy="115" r="14" 
            fill="currentColor" 
            fillOpacity="0.25" 
            stroke="currentColor" 
            strokeWidth="3"
            animate={{ scale: (isListening || isSpeaking) ? [1, 1.35, 1] : [0.95, 1.1, 0.95] }}
            transition={{ duration: (isListening || isSpeaking) ? 0.7 : 2, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: '100px 115px' }}
          />
          <circle cx="100" cy="115" r="6" fill="#ffffff" filter="url(#holoGlow)" className="animate-pulse" />
          <circle cx="100" cy="115" r="2.5" fill="currentColor" />
        </g>

        <g>
          <motion.circle cx="45" cy="30" r="2" fill="#ffffff" animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 1.8, repeat: Infinity }} />
          <motion.circle cx="155" cy="35" r="2.5" fill="#ffffff" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 2.2, repeat: Infinity, delay: 0.4 }} />
          <motion.circle cx="35" cy="130" r="1.5" fill="currentColor" animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.8 }} />
          <motion.circle cx="165" cy="140" r="2" fill="#ffffff" animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 2, repeat: Infinity, delay: 0.2 }} />
          <motion.circle cx="55" cy="220" r="1.5" fill="currentColor" animate={{ opacity: [0.3, 0.9, 0.3] }} transition={{ duration: 2.5, repeat: Infinity }} />
          <motion.circle cx="145" cy="230" r="2" fill="#ffffff" animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.6, repeat: Infinity, delay: 0.6 }} />
        </g>
      </motion.svg>
    );
  }

  // 3. CYBER CAT FACE
  if (effectiveFace === 'cyber_cat') {
    return (
      <motion.svg viewBox="0 0 120 120" className={className} animate={bobAnimation}>
        <motion.circle cx="60" cy="60" r="50" fill="currentColor" animate={voicePulse} initial={{ opacity: 0.08, scale: 1 }} className="blur-xl" />
        <path d="M 30 35 Q 20 10 45 15 Z" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="2" strokeOpacity="0.7" />
        <path d="M 90 35 Q 100 10 75 15 Z" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="2" strokeOpacity="0.7" />
        <path d="M 32 30 Q 25 15 42 18 Z" fill="currentColor" fillOpacity="0.5" />
        <path d="M 88 30 Q 95 15 78 18 Z" fill="currentColor" fillOpacity="0.5" />
        <rect x="15" y="25" width="90" height="75" rx="37.5" fill="#000000" fillOpacity="0.7" stroke="currentColor" strokeWidth="3" strokeOpacity="0.5" />
        <path d="M 20 40 Q 60 25 100 40 A 32 32 0 0 0 20 40 Z" fill="#ffffff" fillOpacity="0.08" />
        <motion.g style={{ transformOrigin: 'center' }} animate={{ scale: (isListening || isSpeaking) ? 1.05 : 1 }} transition={{ duration: 0.3 }}>
          <ellipse cx="32" cy="68" rx="8" ry="5" fill="#f472b6" opacity={(isListening || isSpeaking) ? "0.9" : "0.5"} className={(isListening || isSpeaking) ? "animate-pulse" : ""} />
          <ellipse cx="88" cy="68" rx="8" ry="5" fill="#f472b6" opacity={(isListening || isSpeaking) ? "0.9" : "0.5"} className={(isListening || isSpeaking) ? "animate-pulse" : ""} />
          <motion.ellipse cx="38" cy="55" rx="7" ry="11" fill="currentColor" animate={blinkAnimation} style={{ transformOrigin: '38px 55px' }} />
          <motion.ellipse cx="82" cy="55" rx="7" ry="11" fill="currentColor" animate={blinkAnimation} style={{ transformOrigin: '82px 55px' }} />
          <path d={isSpeaking ? "M 54 73 Q 60 82 66 73 Z" : "M 56 74 Q 60 78 64 74"} fill={isSpeaking ? "currentColor" : "none"} stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </motion.g>
      </motion.svg>
    );
  }

  // 4. QUANTUM ORB FACE
  if (effectiveFace === 'quantum_orb') {
    return (
      <motion.svg viewBox="0 0 120 120" className={className} animate={bobAnimation}>
        <motion.circle cx="60" cy="60" r="48" fill="currentColor" animate={voicePulse} className="blur-2xl" />
        
        <motion.circle 
          cx="60" cy="60" r="44" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="1.5" 
          strokeDasharray="12 18" 
          strokeOpacity="0.6"
          animate={{ rotate: 360 }}
          transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: '60px 60px' }}
        />

        <motion.circle 
          cx="60" cy="60" r="35" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeDasharray="24 10" 
          strokeOpacity="0.8"
          animate={{ rotate: -360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: '60px 60px' }}
        />

        <motion.circle 
          cx="60" cy="60" r="22" 
          fill="currentColor" 
          fillOpacity="0.25"
          stroke="currentColor"
          strokeWidth="3"
          animate={{ scale: (isListening || isSpeaking) ? [1, 1.25, 1] : [0.95, 1.05, 0.95] }}
          transition={{ duration: (isListening || isSpeaking) ? 0.8 : 2.5, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: '60px 60px' }}
        />

        <circle cx="60" cy="60" r="10" fill="#ffffff" fillOpacity="0.9" className="blur-[1px]" />
        <motion.path 
          d="M 60 42 L 64 56 L 78 60 L 64 64 L 60 78 L 56 64 L 42 60 L 56 56 Z" 
          fill="currentColor"
          animate={{ opacity: [0.7, 1, 0.7], rotate: [0, 90, 180] }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: '60px 60px' }}
        />
      </motion.svg>
    );
  }

  // 5. HOLOGRAPHIC ANIME AI FACE
  if (effectiveFace === 'holo_anime') {
    return (
      <motion.svg viewBox="0 0 120 120" className={className} animate={bobAnimation}>
        <motion.circle cx="60" cy="60" r="50" fill="currentColor" animate={voicePulse} className="blur-xl" />
        
        <polygon points="60,15 98,38 88,95 60,110 32,95 22,38" fill="#000000" fillOpacity="0.75" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.7" />
        
        <rect x="42" y="24" width="36" height="8" rx="4" fill="currentColor" fillOpacity="0.4" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="60" cy="28" r="2.5" fill="#ffffff" className="animate-ping" />

        <motion.g animate={{ scale: (isListening || isSpeaking) ? 1.05 : 1 }} style={{ transformOrigin: 'center' }}>
          <motion.path d="M 34 52 Q 44 44 52 54" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
          <motion.ellipse cx="43" cy="62" rx="6" ry="10" fill="currentColor" animate={blinkAnimation} style={{ transformOrigin: '43px 62px' }} />
          <circle cx="41" cy="58" r="2.5" fill="#ffffff" />

          <motion.path d="M 68 54 Q 76 44 86 52" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
          <motion.ellipse cx="77" cy="62" rx="6" ry="10" fill="currentColor" animate={blinkAnimation} style={{ transformOrigin: '77px 62px' }} />
          <circle cx="75" cy="58" r="2.5" fill="#ffffff" />

          <ellipse cx="36" cy="74" rx="7" ry="3.5" fill="#f472b6" opacity="0.8" />
          <ellipse cx="84" cy="74" rx="7" ry="3.5" fill="#f472b6" opacity="0.8" />

          <path d={isSpeaking ? "M 55 82 Q 60 92 65 82 Z" : "M 56 82 Q 60 86 64 82"} fill={isSpeaking ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </motion.g>
      </motion.svg>
    );
  }

  // 6. NEON SYNTH VISOR FACE
  if (effectiveFace === 'neon_synth') {
    return (
      <motion.svg viewBox="0 0 120 120" className={className} animate={bobAnimation}>
        <motion.rect x="10" y="20" width="100" height="80" rx="20" fill="currentColor" initial={{ opacity: 0.15, scale: 1 }} animate={voicePulse} className="blur-xl" />
        
        <rect x="12" y="22" width="96" height="76" rx="18" fill="#05070e" stroke="currentColor" strokeWidth="3" strokeOpacity="0.8" />
        
        <path d="M 20 40 L 100 40 M 20 60 L 100 60 M 20 80 L 100 80" stroke="currentColor" strokeWidth="0.5" strokeOpacity="0.2" />

        <g>
          <motion.rect x="28" y="46" width="6" height={14} rx="3" fill="currentColor" initial={{ height: 14 }} animate={(isListening || isSpeaking) ? { height: [12, 28, 14, 24, 12] } : { height: 14 }} transition={{ duration: 0.5, repeat: Infinity }} />
          <motion.rect x="38" y="42" width="6" height={20} rx="3" fill="currentColor" initial={{ height: 20 }} animate={(isListening || isSpeaking) ? { height: [18, 36, 16, 32, 18] } : { height: 20 }} transition={{ duration: 0.6, repeat: Infinity }} />
          <motion.rect x="48" y="46" width="6" height={14} rx="3" fill="currentColor" initial={{ height: 14 }} animate={(isListening || isSpeaking) ? { height: [10, 26, 14, 22, 10] } : { height: 14 }} transition={{ duration: 0.4, repeat: Infinity }} />

          <motion.rect x="66" y="46" width="6" height={14} rx="3" fill="currentColor" initial={{ height: 14 }} animate={(isListening || isSpeaking) ? { height: [14, 26, 10, 24, 14] } : { height: 14 }} transition={{ duration: 0.5, repeat: Infinity }} />
          <motion.rect x="76" y="42" width="6" height={20} rx="3" fill="currentColor" initial={{ height: 20 }} animate={(isListening || isSpeaking) ? { height: [20, 36, 14, 30, 20] } : { height: 20 }} transition={{ duration: 0.6, repeat: Infinity }} />
          <motion.rect x="86" y="46" width="6" height={14} rx="3" fill="currentColor" initial={{ height: 14 }} animate={(isListening || isSpeaking) ? { height: [12, 28, 16, 22, 12] } : { height: 14 }} transition={{ duration: 0.4, repeat: Infinity }} />
        </g>

        <motion.line 
          x1="16" y1="30" x2="104" y2="30" 
          stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.8"
          animate={{ y: [0, 60, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />

        <rect x="45" y="80" width="30" height="4" rx="2" fill="currentColor" fillOpacity="0.6" />
      </motion.svg>
    );
  }

  // 7. MINIMAL FLUID CORE FACE
  return (
    <motion.svg viewBox="0 0 120 120" className={className} animate={bobAnimation}>
      <defs>
        <radialGradient id="lunaFluidGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="50%" stopColor="currentColor" stopOpacity="0.7" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.1" />
        </radialGradient>
      </defs>

      {(isListening || isSpeaking) && (
        <>
          <motion.circle 
            cx="60" cy="60" r="45" 
            fill="none" stroke="currentColor" strokeWidth="1.5" 
            animate={{ scale: [1, 1.3], opacity: [0.6, 0] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeOut" }}
            style={{ transformOrigin: '60px 60px' }}
          />
          <motion.circle 
            cx="60" cy="60" r="45" 
            fill="none" stroke="currentColor" strokeWidth="1.5" 
            animate={{ scale: [1, 1.3], opacity: [0.6, 0] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeOut", delay: 0.6 }}
            style={{ transformOrigin: '60px 60px' }}
          />
        </>
      )}

      <motion.circle 
        cx="60" cy="60" r="36" 
        fill="url(#lunaFluidGrad)" 
        stroke="currentColor" 
        strokeWidth="2"
        animate={{
          scale: (isListening || isSpeaking) ? [1, 1.12, 0.95, 1.08, 1] : [0.98, 1.02, 0.98],
        }}
        transition={{ duration: (isListening || isSpeaking) ? 1.5 : 4, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: '60px 60px' }}
      />
    </motion.svg>
  );
};

export default LunaFace;
